import he from 'he'
import sanitizeHtml from 'sanitize-html'

// Pasted comment images are uploaded to file-storage and referenced by id only —
// `<img data-filestore-id="<uuid>">`. We never persist a `src` (no inline data
// URIs, no external URLs that rot); the id is resolved to a signed URL at render.
const FILESTORE_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const sanitize = (html: string) =>
  sanitizeHtml(html, {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      img: ['alt', 'width', 'height', 'data-filestore-id'],
      // Merged cells only arrive by pasting a table in — the toolbar has no merge action — and
      // dropping the spans reflows every row after them.
      td: ['colspan', 'rowspan'],
      th: ['colspan', 'rowspan'],
    },
    exclusiveFilter: (frame) => frame.tag === 'img' && !FILESTORE_ID_REGEX.test(frame.attribs['data-filestore-id'] ?? ''),
    allowedClasses: {
      // Quill classes
      '*': [
        'ql-align-right',
        'ql-align-center',
        'ql-align-justify',
        'ql-code-block',
        'ql-code-block-container',
        'ql-syntax',
        'ql-direction-rtl',
        'ql-font-serif',
        'ql-font-monospace',
        'ql-formula',
        'ql-indent-1',
        'ql-indent-2',
        'ql-indent-3',
        'ql-indent-4',
        'ql-indent-5',
        'ql-indent-6',
        'ql-indent-7',
        'ql-indent-8',
        'ql-size-small',
        'ql-size-large',
        'ql-size-huge',
      ],
      // TipTap's table wrapper: the hook the read-only styles hang a horizontal scroll on, so a
      // wide table scrolls in place instead of stretching its container.
      div: ['tableWrapper'],
    },
  })

// Text-free once images are gone: spacer paragraphs, and the layout tables and rows templates build from.
const BLANKABLE_TAGS = new Set(['p', 'div', 'table', 'thead', 'tbody', 'tfoot', 'tr'])

const isBlankBlock = (frame: sanitizeHtml.IFrame) =>
  BLANKABLE_TAGS.has(frame.tag) && frame.text.replaceAll('\u00a0', ' ').trim() === ''

// Counterparty email HTML. No image at all, since a remote one is a tracking pixel. Blank blocks are
// dropped: mail clients pad with empty paragraphs whose `margin:0` inline style does not survive here,
// and templates leave empty layout tables. `title` text would otherwise leak into the body.
export const sanitizeEmailHtml = (html: string) =>
  sanitizeHtml(html, {
    allowedTags: sanitizeHtml.defaults.allowedTags,
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      td: ['colspan', 'rowspan'],
      th: ['colspan', 'rowspan'],
    },
    exclusiveFilter: isBlankBlock,
    // The library's own list, which it does not export, plus `title`.
    nonTextTags: ['script', 'style', 'textarea', 'option', 'xmp', 'title'],
  })

export const removeHtml = (value: string) =>
  sanitizeHtml(value, {
    allowedTags: [],
    allowedAttributes: {},
  })

// Closing tags fire innermost-first, so the unconditional overwrite below encodes
// row > cell > block precedence: TipTap wraps every cell's content in a <p>, and that
// </p> must not claim the separator that the </td> after it needs.
const ROW_TAGS = new Set(['tr', 'caption', 'thead', 'tbody', 'tfoot', 'table'])
const CELL_TAGS = new Set(['td', 'th'])
const BLOCK_TAGS = new Set(['p', 'div', 'li', 'ul', 'ol', 'blockquote', 'pre', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'])

// A decoded &nbsp; is invisible to the whitespace collapse in htmlToText and would otherwise reach
// consumers as a stray U+00A0 — spreadsheet cells and LLM prompts both want a plain space.
const NON_BREAKING_SPACE = /\u00a0/g

/** Flattens rich text to plain text, keeping row/cell/block boundaries as separators. */
export const htmlToText = (html: string) => {
  let pending = ''

  const flattened = sanitizeHtml(html, {
    allowedTags: [],
    allowedAttributes: {},
    onOpenTag: (tagName) => {
      // Accumulated, not overwritten: consecutive <br> are an authored blank line. The collapse
      // below caps a run at one such line.
      if (tagName === 'br') pending += '\n'
    },
    onCloseTag: (tagName) => {
      if (ROW_TAGS.has(tagName) || BLOCK_TAGS.has(tagName)) pending = '\n'
      else if (CELL_TAGS.has(tagName)) pending = ' '
    },
    textFilter: (text) => {
      const prefix = pending
      pending = ''
      return prefix + text
    },
  })

  return he
    .decode(flattened)
    .replace(NON_BREAKING_SPACE, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
